/**
 * Die Stellschrauben der Projektion.
 *
 * Sie gehoeren dem Werkzeug, nicht der Person: was hier steht, gilt fuer alle,
 * die auf dieselbe Projektion schauen. Persoenliches liegt auf konto.elbklar.com
 * (ADR-0005).
 */

import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Betragsfeld, Button, toast } from '@elbklar/ui';

import { TextField } from '@/components/Felder';
import { settingsApi } from '@/lib/api';
import { betragFehler } from '@/lib/betrag';
import { ganzzahl, hatFehler, ohne, unlesbareFelder, zumErstenFehler, type Fehler } from '@/lib/pruefen';
import type { FinanceSettings } from '@/types';

export function ProjektionForm({ einstellungen }: { einstellungen: FinanceSettings }) {
  const queryClient = useQueryClient();

  // Decimal-Text aus dem Betragsfeld, `null` solange keine Zahl dasteht.
  const [puffer, setPuffer] = useState<string | null>('');
  const [fehler, setFehler] = useState<Fehler<'puffer' | 'horizont' | 'rueckblick'>>({});
  const [horizont, setHorizont] = useState('');
  const [rueckblick, setRueckblick] = useState('');

  useEffect(() => {
    setPuffer(einstellungen.min_buffer);
    setHorizont(String(einstellungen.horizon_months));
    setRueckblick(String(einstellungen.burn_lookback_months));
    setFehler({});
  }, [einstellungen]);

  const speichern = useMutation({
    // Decimal-Text aus dem Betragsfeld, von `onSubmit` geprüft.
    mutationFn: (mindestpuffer: string) =>
      settingsApi.update({
        min_buffer: mindestpuffer,
        horizon_months: Number(horizont),
        burn_lookback_months: Number(rueckblick),
      }),
    onSuccess: () => {
      toast.success('Einstellungen gespeichert');
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
      void queryClient.invalidateQueries({ queryKey: ['projection'] });
    },
    onError: () => toast.error('Speichern fehlgeschlagen'),
  });

  return (
    <form noValidate
      onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // Grenzen aus FinanceSettingsUpdate (finance-api).
        const unlesbar = unlesbareFelder(e.currentTarget);
        const neu = {
          puffer: betragFehler(puffer),
          horizont: ganzzahl(horizont, { min: 1, max: 120, pflicht: true, unlesbar: unlesbar.has('horizont') }),
          rueckblick: ganzzahl(rueckblick, { min: 1, max: 24, pflicht: true, unlesbar: unlesbar.has('rueckblick') }),
        };
        setFehler(neu);
        if (hatFehler(neu) || puffer === null) {
          zumErstenFehler(e.currentTarget);
          return;
        }
        speichern.mutate(puffer);
      }}
      className="max-w-2xl space-y-4"
    >
      <Betragsfeld
        label="Mindestpuffer (€)"
        hinweis={() => 'Unterschreitet die Projektion diesen Wert, gilt es als Engpass. Die Warnung kommt damit deutlich vor der Zahlungsunfähigkeit.'}
        error={fehler.puffer}
        required
        value={puffer ?? ''}
        onChange={(wert) => {
          setFehler((f) => ohne(f, 'puffer'));
          setPuffer(wert);
        }}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          label="Planungshorizont (Monate)"
          type="number"
          name="horizont"
          min={1}
          max={120}
          value={horizont}
          onChange={(e) => {
            setHorizont(e.target.value);
            setFehler((f) => ohne(f, 'horizont'));
          }}
          error={fehler.horizont}
          required
        />
        <TextField
          label="Burn-Fenster (Monate)"
          type="number"
          name="rueckblick"
          min={1}
          max={24}
          hint="Rückblick für den Ist-Burn"
          value={rueckblick}
          onChange={(e) => {
            setRueckblick(e.target.value);
            setFehler((f) => ohne(f, 'rueckblick'));
          }}
          error={fehler.rueckblick}
          required
        />
      </div>

      <Button type="submit" variant="primary" loading={speichern.isPending}>
        Einstellungen speichern
      </Button>
    </form>
  );
}
